import { Skeleton } from '@mui/material';
import { MetricCard, Text } from '@fragments';
import {
    IconCashBanknote,
    IconCoin,
    IconReceiptRefund,
    IconShieldDollar,
    IconReceipt,
    IconCalculator,
    IconAlertTriangle,
} from '@tabler/icons-react';

import SalesSplitCard from '../SalesSplitCard';

import './styles.scss';

function toMajor(cents) {
    if (cents == null) return 0;
    return Math.round(Number(cents)) / 100;
}

function sentimentByThreshold(percentile, green, red) {
    const p = Number(percentile) || 0;
    if (p < green) return 'positive';
    if (p >= red) return 'negative';
    return 'warning';
}

function MetricsSkeleton() {
    return (
        <div className="insights-metrics__columns">
            <div className="insights-metrics__col">
                <Skeleton variant="rounded" height={140} className="insights-metrics__skeleton" />
                {[0, 1, 2].map(i => (
                    <div className="insights-metrics__pair" key={i}>
                        <Skeleton variant="rounded" height={140} className="insights-metrics__skeleton" />
                        <Skeleton variant="rounded" height={140} className="insights-metrics__skeleton" />
                    </div>
                ))}
            </div>
            <div className="insights-metrics__col insights-metrics__col--donut">
                <Skeleton variant="rounded" height={600} className="insights-metrics__skeleton" />
            </div>
        </div>
    );
}

// Two-column summary for the selected currency. Left: the volume/revenue ladder;
// right: the national-vs-international split donut (spans both currencies) over
// the refund/chargeback risk pair. `summary` is null-safe — a currency with no
// charges renders zeros rather than blanking, while the donut still shows the mix.
export default function MetricsSection({ report, currency, reportLoading }) {
    const currencyReport = report?.currencyReports?.find(r => r.currency === currency) || null;
    const summary = currencyReport?.summary || null;
    const revenue = summary?.revenue;

    // Per-bucket sales counts over the period → the "Total de Vendas" sparkline.
    const salesTrend = (currencyReport?.salesAmount?.data || []).map(p => Number(p.value) || 0);

    return (
        <div className="insights-metrics">
            <Text.h4 contrast={7} className="insights-metrics__heading mb-2">
                Resumo do período
            </Text.h4>

            {reportLoading && !report ? (
                <MetricsSkeleton />
            ) : (
                <div className={`insights-metrics__columns${reportLoading ? ' insights-metrics__columns--reloading' : ''}`}>
                    <div className="insights-metrics__col">
                        <MetricCard
                            title="Total de Vendas"
                            leftIcon={<IconReceipt size={15} />}
                            value={summary?.salesTotal ?? 0}
                            variant="number"
                            chartValues={salesTrend}
                            size="ultralarge"
                            description="Quantidade de cobranças pagas no período."
                            loading={reportLoading}
                        />
                        <div className="insights-metrics__pair">
                            <MetricCard
                                title="Receita bruta"
                                leftIcon={<IconCashBanknote size={15} />}
                                value={toMajor(revenue?.total)}
                                variant="money"
                                currency={currency}
                                description="Soma das cobranças pagas, reembolsadas ou revertidas no período, incluindo as taxas da plataforma."
                                loading={reportLoading}
                            />
                            <MetricCard
                                title="Receita líquida"
                                leftIcon={<IconCoin size={15} />}
                                value={toMajor(revenue?.liquid)}
                                variant="money"
                                currency={currency}
                                description="Soma das comissões de todos os recebedores."
                                loading={reportLoading}
                            />
                        </div>
                        <div className="insights-metrics__pair">
                            <MetricCard
                                title="Sua comissão"
                                leftIcon={<IconShieldDollar size={15} />}
                                value={toMajor(revenue?.commission)}
                                variant="money"
                                currency={currency}
                                description="Comissão atribuída a você."
                                loading={reportLoading}
                            />
                            <MetricCard
                                title="Ticket médio"
                                leftIcon={<IconCalculator size={15} />}
                                value={toMajor(revenue?.averageTicket)}
                                variant="money"
                                currency={currency}
                                description="Receita bruta / quantidade de cobranças pagas."
                                loading={reportLoading}
                            />
                        </div>
                        <div className="insights-metrics__pair">
                            <MetricCard
                                title="Reembolsos"
                                leftIcon={<IconReceiptRefund size={15} />}
                                value={toMajor(summary?.refund?.amount)}
                                variant="money"
                                currency={currency}
                                note={`${Math.round((summary?.refund?.percentile ?? 0) * 10) / 10}% das vendas no período`}
                                noteSentiment={sentimentByThreshold(summary?.refund?.percentile, 4.5, 6)}
                                description="Valor reembolsado e representação sobre o total de vendas."
                                loading={reportLoading}
                            />
                            <MetricCard
                                title="Chargebacks"
                                leftIcon={<IconAlertTriangle size={15} />}
                                value={toMajor(summary?.chargeback?.amount)}
                                variant="money"
                                currency={currency}
                                note={`${Math.round((summary?.chargeback?.percentile ?? 0) * 10) / 10}% das vendas no período`}
                                noteSentiment={sentimentByThreshold(summary?.chargeback?.percentile, 1, 1.5)}
                                description="Valor contestado e representação sobre o total de vendas."
                                loading={reportLoading}
                            />
                        </div>
                    </div>

                    <div className="insights-metrics__col insights-metrics__col--donut">
                        <SalesSplitCard report={report} reportLoading={reportLoading} />
                    </div>
                </div>
            )}
        </div>
    );
}
